/**
 * @fileoverview
 * Authentication Service - Manages user authentication
 * Uses Angular Signals for reactive state management
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, map } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../constants';
import { StorageUtil } from '../../utils';
import { TokenService } from './token.service';
import { PermissionService } from './permission.service';
import { SystemConfigCacheService } from '@core/services/system-config-cache.service';
import { NotificationWebSocketService } from '@core/services/notification-websocket.service';
import { NotificationApiService } from '@core/services/notification-api.service';
import { SelectedBranchService } from '@core/services/selected-branch.service';
import { SelectedAuditTypeService } from '@core/services/selected-audit-type.service';
import {
  User,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UserType
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly permissionService = inject(PermissionService);
  private readonly systemConfigCache = inject(SystemConfigCacheService);
  private readonly notificationWs = inject(NotificationWebSocketService);
  private readonly notificationApi = inject(NotificationApiService);
  private readonly selectedBranchService = inject(SelectedBranchService);
  private readonly selectedAuditTypeService = inject(SelectedAuditTypeService);

  // Signals for reactive state
  private readonly userSignal = signal<User | null>(this.loadUserFromStorage());
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // Public readonly signals
  readonly user = this.userSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  
  // Computed signals
  readonly isAuthenticated = computed(() => !!this.userSignal());
  readonly userName = computed(() => {
    const user = this.userSignal();
    return user ? `${user.userName}` : '';
  });
  readonly userFullName = computed(() => {
    const user = this.userSignal();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });
  readonly userRole = computed(() => this.userSignal()?.roleName || '');
  constructor() {
    // Auto-login if token exists and is valid
    this.checkAuthentication();
  }

  /**
   * Login with email or employee ID and password
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    return this.http.post<LoginResponse>(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.LOGIN}`,
      credentials
    ).pipe(
      tap(response => {
        this.handleLoginSuccess(response);
        this.loadingSignal.set(false);
      }),
      catchError(error => {
        this.handleLoginError(error);
        this.loadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout current user
   */
  logout(): void {
    this.loadingSignal.set(true);

    // Call backend logout endpoint
    this.http.post(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.LOGOUT}`,
      {}
    ).subscribe({
      complete: () => this.handleLogout()
    });

    // Always clear local data, even if API call fails
    this.handleLogout();
  }

  /**
   * Refresh access token
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    const refreshToken = this.tokenService.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };

    return this.http.post<RefreshTokenResponse>(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.REFRESH}`,
      request
    ).pipe(
      tap(response => {
        this.tokenService.setToken(response.accessToken);
        if (response.csrfToken) {
          this.tokenService.setCsrfToken(response.csrfToken);
        }
      }),
      catchError(error => {
        this.handleLogout();
        return throwError(() => error);
      })
    );
  }

  /**
   * Forgot password request
   */
  forgotPassword(request: ForgotPasswordRequest): Observable<{ message: string }> {
    this.loadingSignal.set(true);

    return this.http.post<{ message: string }>(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`,
      request
    ).pipe(
      tap(() => this.loadingSignal.set(false)),
      catchError(error => {
        this.loadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Reset password with token
   */
  resetPassword(request: ResetPasswordRequest): Observable<{ message: string }> {
    this.loadingSignal.set(true);

    return this.http.post<{ message: string }>(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`,
      request
    ).pipe(
      tap(() => this.loadingSignal.set(false)),
      catchError(error => {
        this.loadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current user from backend
   */
  getCurrentUser(): Observable<User> {
    return this.http.get<User>(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.ME}`
    ).pipe(
      tap(user => {
        this.userSignal.set(user);
        this.saveUserToStorage(user);
        
        // Fetch permissions after getting user data
        this.permissionService.fetchPermissions().subscribe({
          error: (error) => {
            // Log error but don't fail user fetch
            console.error('Failed to fetch permissions:', error);
          }
        });
        this.systemConfigCache.fetchAndCache().subscribe({
          error: (error) => {
            console.error('Failed to fetch system config:', error);
          }
        });
      })
    );
  }

  /**
   * Check if user has permission
   */
  hasPermission(permission: string): boolean {
    return this.permissionService.hasPermission(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return this.permissionService.hasAnyPermission(permissions);
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return this.permissionService.hasAllPermissions(permissions);
  }

  /**
   * Refresh user permissions
   */
  refreshPermissions(): Observable<any> {
    return this.permissionService.refreshPermissions();
  }

  /**
   * Check if user has role
   */
  hasRole(role: string): boolean {
    const user = this.userSignal();
    return user?.roleName === role;
  }

  /**
   * Fetch CSRF token from backend
   * Should be called on app initialization
   */
  fetchCsrfToken(): Observable<void> {
    return this.http.get<{ csrfToken: string }>(`${environment.apiUrl}${API_ENDPOINTS.AUTH.CSRF_TOKEN}`).pipe(
      tap(response => {
        if (response?.csrfToken) {
          this.tokenService.setCsrfToken(response.csrfToken);
          console.log('✅ CSRF token fetched and stored');
        }
      }),
      map(() => void 0), // Convert response to void
      catchError(error => {
        console.error('❌ Failed to fetch CSRF token:', error);
        // Don't fail the app if CSRF token fetch fails
        return throwError(() => error);
      })
    );
  }

  /**
   * Handle successful login
   */
  private handleLoginSuccess(response: LoginResponse): void {
    // Save tokens
    this.tokenService.setToken(response.accessToken);
    this.tokenService.setRefreshToken(response.refreshToken);
    
    // Save CSRF token if provided in login response
    if (response.csrfToken) {
      this.tokenService.setCsrfToken(response.csrfToken);
    }

    // Save user data
    this.userSignal.set(response.user);
    this.saveUserToStorage(response.user);

    // Clear any errors
    this.errorSignal.set(null);

    // Fetch CSRF token for subsequent requests (requires authentication)
    this.fetchCsrfToken().subscribe({
      error: (error) => {
        console.warn('Failed to fetch CSRF token:', error);
        // Non-critical - continue anyway
      }
    });

    // Fetch user permissions after successful login
    this.permissionService.fetchPermissions().subscribe({
      error: (error) => {
        // Log error but don't fail login
        console.error('Failed to fetch permissions:', error);
      }
    });

    this.systemConfigCache.fetchAndCache().subscribe({
      error: (error) => {
        console.error('Failed to fetch system config:', error);
      }
    });

    // Load in-app notifications so they appear in the header bell
    this.notificationApi.loadHistory().subscribe();

    // Auto-select nearest accessible branch for the application header
    this.selectedBranchService.initializeAfterAuth({ forceNearest: true });
  }

  /**
   * Handle login error
   */
  private handleLoginError(error: any): void {
    // Use standardized error format (extracted by error interceptor)
    const errorMessage = error.errorMessage || error.error?.message || 'Login failed. Please try again.';
    this.errorSignal.set(errorMessage);
  }

  /**
   * Handle logout
   */
  private handleLogout(): void {
    this.notificationWs.disconnect();
    // Clear tokens
    this.tokenService.removeTokens();

    // Clear user data
    this.userSignal.set(null);
    StorageUtil.removeLocal(STORAGE_KEYS.USER_DATA);

    // Clear permissions
    this.permissionService.clearPermissions();
    this.systemConfigCache.clearCache();
    this.selectedBranchService.clear();
    this.selectedAuditTypeService.clear();

    // Clear loading and error states
    this.loadingSignal.set(false);
    this.errorSignal.set(null);

    // Redirect to login
    this.router.navigate(['/auth/login']);
  }

  /**
   * Save user to storage
   */
  private saveUserToStorage(user: User): void {
    StorageUtil.setLocal(STORAGE_KEYS.USER_DATA, user);
  }

  /**
   * Load user from storage
   */
  private loadUserFromStorage(): User | null {
    return StorageUtil.getLocal<User>(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Check authentication status on app init
   */
  private checkAuthentication(): void {
    const hasToken = this.tokenService.hasToken();
    const isExpired = this.tokenService.isTokenExpired();

    if (hasToken && !isExpired) {
      // Token exists and is valid, user is authenticated
      const user = this.loadUserFromStorage();
      if (user) {
        this.userSignal.set(user);
        // Fetch permissions when restoring session
        this.permissionService.fetchPermissions().subscribe({
          error: (error) => {
            // Log error but don't fail session restore
            console.error('Failed to fetch permissions:', error);
          }
        });
        this.systemConfigCache.fetchAndCache().subscribe({
          error: (error) => {
            console.error('Failed to fetch system config:', error);
          }
        });
        this.selectedBranchService.initializeAfterAuth({ forceNearest: false });
      } else {
        // Fetch user data from backend (this will also fetch permissions)
        this.getCurrentUser().subscribe({
          next: () => this.selectedBranchService.initializeAfterAuth({ forceNearest: false }),
          error: () => this.handleLogout()
        });
      }
    } else if (hasToken && isExpired) {
      // Token expired, try to refresh
      this.refreshToken().subscribe({
        next: () => {
          // After refresh, get current user (this will also fetch permissions)
          this.getCurrentUser().subscribe({
            next: () => this.selectedBranchService.initializeAfterAuth({ forceNearest: false }),
          });
        },
        error: () => this.handleLogout()
      });
    } else {
      // No token, user is not authenticated
      this.userSignal.set(null);
    }
  }

  isSuperAdmin(): boolean {
    return this.user()?.userType === UserType.SUPER_ADMIN;
  }
}